import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { Icone } from './Icone'

/**
 * Les messages qui passent — RG-D27, RG-D28, RG-D29, [DECISIONS §338].
 *
 * **L'application n'en avait aucun, et cela se voyait sur les gestes dont
 * l'effet n'est pas à l'écran.** Un ami retiré, une préférence enregistrée, un
 * thème suivi : quand le serveur refusait, le composant remettait
 * silencieusement l'état d'avant. La case revenait toute seule à sa position
 * initiale, sans un mot — et le candidat concluait que son doigt avait raté la
 * cible. Il retapait. C'est le pire des retours possibles : celui qui fait
 * refaire un geste qui n'a pas échoué par la faute de qui l'a fait.
 *
 * **Ce qu'un toast N'EST PAS.** Il ne remplace jamais une erreur de champ. Une
 * faute de saisie s'écrit sous le champ, là où la main est déjà, et elle y
 * reste tant qu'elle n'est pas corrigée (`Field`, RG-D18). Le toast dit ce qui
 * est arrivé à une ACTION — c'est-à-dire à quelque chose dont le résultat vit
 * ailleurs que sous le doigt.
 *
 * **Deux régions, pas une.** Le niveau d'urgence d'une annonce se déclare sur
 * la RÉGION `aria-live`, pas sur le message : une seule région obligerait à
 * choisir `polite` ou `assertive` une fois pour toutes, et un refus annoncé
 * poliment attend que le lecteur d'écran ait fini sa phrase — parfois trente
 * secondes plus tard, quand le toast a disparu.
 *
 * **Les deux régions sont montées EN PERMANENCE**, vides la plupart du temps.
 * C'est le point qu'on rate toujours : une région `aria-live` insérée dans le
 * document EN MÊME TEMPS que son contenu n'est pas annoncée — le lecteur
 * d'écran n'observe que les régions qu'il connaissait déjà. Un toast parfait
 * qui ne se dit jamais est un toast absent.
 *
 * **Aucune dépendance.** Il n'y a ni Node ni npm sur la machine où ce dépôt
 * s'édite : tout ce qui suit est React et l'API du navigateur.
 */

export type TonDuToast = 'confirmation' | 'refus'

interface Toast {
  id: number
  ton: TonDuToast
  texte: string
}

/** Ce qu'un écran peut dire. Deux verbes, et rien de plus à décider. */
export interface PosteDeToasts {
  /** Ce qui a marché. S'efface tout seul. */
  confirmer: (texte: string) => void
  /** Ce qui n'a pas marché. Reste jusqu'à ce qu'on le ferme. */
  refuser: (texte: string) => void
}

const CanalDesToasts = createContext<PosteDeToasts | null>(null)

/**
 * **Trois à l'écran au plus.**
 *
 * Une salve — retirer cinq envois d'affilée hors connexion — en poserait cinq,
 * et cinq cartes empilées couvrent la moitié d'un écran de téléphone, soit
 * exactement ce que le §298 a passé son temps à dégager. Au-delà, c'est le plus
 * ANCIEN qui part : le dernier message est celui du dernier geste.
 */
const PILE_MAXIMALE = 3

/**
 * Combien de temps une confirmation reste — jamais moins de quatre secondes.
 *
 * **La durée suit la longueur, parce que la lecture aussi.** Une durée fixe est
 * soit trop courte pour la phrase la plus longue, soit inutilement longue pour
 * « Enregistré ». Soixante millisecondes par caractère est le rythme d'une
 * lecture posée — celle de quelqu'un qui ne s'attendait pas à lire. Le plafond
 * de dix secondes existe parce qu'au-delà le message n'est plus un passage :
 * il est un élément d'interface que rien ne retire.
 */
function dureeDe(texte: string): number {
  return Math.min(10_000, 4_000 + texte.length * 60)
}

/**
 * Le fournisseur — une seule fois, au sommet de l'application.
 *
 * Il rend ses enfants tels quels et pose la pile PAR-DESSUS, en `fixed`. Rien
 * de ce qui suit ne dépend de l'écran affiché : c'est ce qui permet à une
 * action lancée dans une feuille modale de parler encore après sa fermeture.
 */
export function FournisseurDeToasts({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const dernierId = useRef(0)

  const retirer = useCallback((id: number): void => {
    setToasts((avant) => avant.filter((toast) => toast.id !== id))
  }, [])

  const poser = useCallback((ton: TonDuToast, texte: string): void => {
    dernierId.current += 1

    const frais: Toast = { id: dernierId.current, ton, texte }

    setToasts((avant) =>
      /*
       * **Le même message deux fois ne fait pas deux cartes.**
       *
       * Un bouton qu'on retape parce que rien ne semble se passer envoie deux
       * fois la même requête, donc deux fois le même refus. Empiler « Ce code
       * n'est pas utilisable » sur « Ce code n'est pas utilisable » ne dit rien
       * de plus et occupe deux fois la place. Le doublon est REMPLACÉ, pas
       * ignoré : la carte remonte au bas de la pile et son minuteur repart,
       * donc le message répond bien au dernier geste.
       */
      [...avant.filter((toast) => toast.texte !== texte || toast.ton !== ton), frais].slice(
        -PILE_MAXIMALE,
      ),
    )
  }, [])

  const poste = useMemo<PosteDeToasts>(
    () => ({
      confirmer: (texte: string) => { poser('confirmation', texte); },
      refuser: (texte: string) => { poser('refus', texte); },
    }),
    [poser],
  )

  const confirmations = toasts.filter((toast) => toast.ton === 'confirmation')
  const refus = toasts.filter((toast) => toast.ton === 'refus')

  return (
    <CanalDesToasts.Provider value={poste}>
      {children}

      <div
        /*
         * **Il s'arrête AU-DESSUS de la barre d'onglets, bouton rond compris.**
         *
         * 53 px de barre (`BottomTabs`), plus les 28 px dont le bouton central
         * déborde vers le haut, plus 8 px de jeu. Calé sur la seule barre, le
         * toast coupait le « + » en deux — et un bouton à moitié recouvert se
         * lit comme un défaut d'affichage, pas comme un message.
         *
         * Au-delà de 688 px la barre disparaît au profit du rail latéral, et il
         * ne reste que la marge.
         *
         * `pointer-events-none` sur le CADRE : il couvre toute la largeur de
         * l'écran et avalerait sinon les tapes sur ce qu'il survole. Chaque
         * carte le rétablit pour elle-même.
         */
        className={
          'pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-center gap-2 ' +
          'px-3 pb-[calc(89px+env(safe-area-inset-bottom,0px))] min-[688px]:pb-4'
        }
      >
        {/* **Les deux régions existent même vides** — voir l'en-tête du fichier.
            `aria-atomic="false"` : on n'annonce que la carte QUI ARRIVE, et non
            la pile entière relue depuis le début à chaque ajout. */}
        <div aria-live="polite" aria-atomic="false" className="flex w-full flex-col items-center gap-2">
          {confirmations.map((toast) => (
            <Carte key={toast.id} toast={toast} onFermer={retirer} />
          ))}
        </div>

        <div
          aria-live="assertive"
          aria-atomic="false"
          className="flex w-full flex-col items-center gap-2"
        >
          {refus.map((toast) => (
            <Carte key={toast.id} toast={toast} onFermer={retirer} />
          ))}
        </div>
      </div>
    </CanalDesToasts.Provider>
  )
}

/**
 * Une carte.
 *
 * **La confirmation s'efface, le refus reste.** Une bonne nouvelle a été lue ou
 * ne servait à rien ; une mauvaise doit avoir été lue avant de partir. Un refus
 * qui disparaît au bout de cinq secondes est un refus que la moitié des gens
 * n'auront jamais vu — ils regardaient ailleurs, ils lisaient lentement, ils
 * étaient en train de retaper. Il part quand on le ferme, et pas avant.
 */
function Carte({ toast, onFermer }: { toast: Toast; onFermer: (id: number) => void }) {
  const confirmation = toast.ton === 'confirmation'

  useEffect(() => {
    if (!confirmation) return

    const minuteur = setTimeout(() => { onFermer(toast.id); }, dureeDe(toast.texte))

    return () => { clearTimeout(minuteur); }
  }, [confirmation, toast.id, toast.texte, onFermer])

  const teinte = confirmation ? 'var(--juste-fort)' : 'var(--faux-fort)'

  return (
    <div
      data-toast={toast.ton}
      className="pointer-events-auto flex w-full max-w-[420px] items-start gap-2 rounded-2xl border-2 p-3 shadow-lg"
      style={{
        /*
         * **Fond de page et bordure teintée, plutôt qu'un aplat de couleur.**
         *
         * Un aplat aurait imposé `--sur-juste` / `--sur-faux` au texte : ces
         * deux jetons tiennent le contraste, mais un rectangle rouge vif au bas
         * de l'écran se lit comme une panne de l'application, quand le message
         * ne dit souvent que « réessayez ». La couleur reste — dans le trait et
         * dans l'icône, où RG-D2 la veut accompagnée d'un signe — et le texte
         * garde le contraste garanti du thème.
         */
        background: 'var(--fond)',
        borderColor: teinte,
        color: 'var(--texte-1)',
        // L'apparition est déclarée ICI et non en classe : `prefers-reduced-
        // motion` l'arrête par la règle globale d'`index.css`, et l'état final
        // — opaque, non déplacé — est celui du style statique. Une carte ne peut
        // donc pas rester invisible, quel que soit le réglage.
        animation: 'upfac-toast 160ms ease-out',
      }}
    >
      <span style={{ color: teinte }} className="mt-0.5 flex">
        <Icone nom={confirmation ? 'coche' : 'alerte'} taille={18} />
      </span>

      <p className="min-w-0 flex-1 text-[15px]">{toast.texte}</p>

      {/* RG-11 — 44 px de zone tactile. Les marges négatives la rendent sans
          gonfler la carte : le carré déborde dans le rembourrage déjà là. */}
      <button
        type="button"
        aria-label="Fermer ce message"
        onClick={() => { onFermer(toast.id); }}
        className="-my-2 -mr-2 flex size-11 shrink-0 items-center justify-center"
        style={{ color: 'var(--texte-2)' }}
      >
        <Icone nom="croix" taille={16} />
      </button>
    </div>
  )
}

/**
 * Le poste, pour un écran.
 *
 * **Il lève quand le fournisseur manque, et c'est délibéré.** Rendre un objet
 * inerte aurait fait disparaître des messages sans que rien ne le signale —
 * exactement le défaut que ce fichier existe pour corriger, mais un étage plus
 * bas et invisible aux tests. Une exception se voit au premier montage.
 */
export function useToasts(): PosteDeToasts {
  const poste = useContext(CanalDesToasts)

  if (poste === null) {
    throw new Error(
      'useToasts hors de <FournisseurDeToasts> : le message n’aurait été affiché nulle part.',
    )
  }

  return poste
}
